import { useEffect, useRef, useState } from "react";
import ExerciseMenu from "../../ExerciseMenu";
import { motion } from "framer-motion";
import { RocketLaunch } from "@mui/icons-material";
import { Box, CircularProgress, Typography } from "@mui/material";
import { ExerciseInterface } from "../../../../interfaces/examInterfaces";
import type { Identifier } from "dnd-core";
import { useDrag, useDrop } from "react-dnd";
import { COLORS, DnDItemTypes } from "../../../../constants";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../..";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import { DIALOGS } from "../../../Dialogs/dialogReducer";
import { MANIPULATE_EXERCISES_TYPES } from "../../Reducers/ManipulateExercisesReducer";
import { handleOpenDialog } from "../../Functions/FunctionsMain";
import { changePoints, moveExercise } from "../../Functions/FunctionsManipulateExercises";
import {
  AddExercise,
  AddExerciseContainer,
  BoxNumber,
  ExerciseContainer,
  ExerciseImage,
  ExerciseNumber,
  ExercisePoints,
  LineAddExercise,
  PointsContainer,
  PointsTextField,
  Rocket,
} from "./AllalinhornStyles";
import { Level, StickerNew } from "../../Styles";

export const AllalinhornExercise = ({
  exercise,
  amountExercises,
  imageUrl,
  solutionImageUrl,
  sortedExercises,
  showStickerNew,
  groupColorLight,
}: {
  exercise: ExerciseInterface;
  amountExercises: number;
  imageUrl: string;
  solutionImageUrl: string;
  sortedExercises: ExerciseInterface[];
  showStickerNew: Boolean;
  groupColorLight: string;
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const showSolutions = useSelector((state: RootState) => state.examBuilder.main.showSolutions);
  const selectedObjectiveId = useSelector((state: RootState) => state.examBuilder.main.selectedObjectiveId);
  const examData = useSelector((state: RootState) => state.examBuilder.main.examData);
  const zoomFactor = useSelector((state: RootState) => state.examBuilder.main.zoomFactor);

  const dispatch = useDispatch();

  useEffect(() => {
    setIsLoading(true);
  }, [showSolutions]);

  function toggleMenu(showMenu: boolean) {
    setShowMenu(showMenu);
  }

  //dnd logic
  let id = exercise.id;
  let index = exercise.index;

  const [{ handlerId }, drop] = useDrop<ExerciseInterface, void, { handlerId: Identifier | null }>({
    accept: DnDItemTypes.EXERCISE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: ExerciseInterface, monitor) {
      if (!containerRef.current) {
        return;
      }
      const dragId = item.id;
      const hoverIndex = index;

      if (dragId === id) {
        return;
      }

      const hoverBoundingRect = containerRef.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      if (!hoverBoundingRect || !clientOffset) {
        return;
      }

      // Get vertical middle of the hovered element
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // Get the mouse position relative to the hovered element
      const clientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the item's height
      if (item.index < hoverIndex && clientY < hoverMiddleY) {
        return;
      }
      if (item.index > hoverIndex && clientY > hoverMiddleY) {
        return;
      }

      if (selectedObjectiveId) {
        moveExercise(item.index, hoverIndex, sortedExercises, selectedObjectiveId, examData);
      }
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: DnDItemTypes.EXERCISE,
    item: () => {
      return { id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.getItem()?.id === id,
    }),
  });

  const opacity = isDragging ? 0.6 : 1;

  drag(drop(containerRef));

  function openAddExerciseDialog(exerciseIndex: number) {
    const indexNewExercises = exerciseIndex + 1;
    setIndex(indexNewExercises);
    handleOpenDialog(DIALOGS.SELECT_EXERCISES_EXAM_DIALOG);
  }

  function setIndex(index: number) {
    dispatch({ type: MANIPULATE_EXERCISES_TYPES.INDEX_NEW_EXERCISES, payload: index });
  }

  function handleChangePoints(newPoints: string) {
    if (selectedObjectiveId) {
      changePoints(exercise.index, newPoints, sortedExercises, selectedObjectiveId, examData);
    }
  }

  function checkInputHasValue(newPoints: string) {
    if (newPoints === "") {
      newPoints = "1";
      if (selectedObjectiveId) {
        changePoints(exercise.index, newPoints, sortedExercises, selectedObjectiveId, examData);
      }
    }
  }

  return (
    <>
      <Box ref={containerRef} style={{ opacity }} data-handler-id={handlerId}>
        <ExerciseContainer
          onMouseEnter={() => toggleMenu(true)}
          onMouseLeave={() => toggleMenu(false)}
          zoomfactor={zoomFactor}
        >
          <AddExerciseContainer
            onMouseEnter={() => (setShowAddExercise(true), toggleMenu(false))}
            onMouseLeave={() => (setShowAddExercise(false), toggleMenu(true))}
            onClick={() => openAddExerciseDialog(exercise.index)}
            zoomfactor={zoomFactor}
          >
            {showAddExercise && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{ width: "100%", display: "flex", justifyContent: "center" }}
              >
                <AddExercise>
                  <LineAddExercise />
                  <NoteAddIcon></NoteAddIcon>
                  <Typography>Aufgaben hinzufügen</Typography>
                  <LineAddExercise />
                </AddExercise>
              </motion.div>
            )}
          </AddExerciseContainer>
          <BoxNumber zoomfactor={zoomFactor}>
            <ExerciseNumber zoomfactor={zoomFactor} selectedColor={groupColorLight}>{exercise.index + 1}.</ExerciseNumber>
            {showMenu && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Level zoomfactor={zoomFactor}>N{exercise.level}</Level>
                {showStickerNew && <StickerNew zoomfactor={zoomFactor}>Neu</StickerNew>}
              </motion.div>
            )}
          </BoxNumber>

          <ExerciseImage zoomfactor={zoomFactor} style={{ position: "relative" }}>
            {isLoading && (
              <CircularProgress
                sx={{
                  color: showSolutions ? COLORS.DIDACTO_DARK_RED : COLORS.DIDACTO_DARK_BLUE,
                  position: "absolute",
                  top: 0,
                  left: 0,
                  zIndex: 1,
                }}
              />
            )}
            <img
              src={showSolutions ? solutionImageUrl : imageUrl}
              alt="Exercise"
              onLoad={() => setIsLoading(false)}
              style={{
                width: "100%",
                visibility: isLoading ? "hidden" : "visible",
              }}
            />
          </ExerciseImage>
          {exercise.showRocket && (
            <Rocket zoomfactor={zoomFactor}>
              <RocketLaunch />
            </Rocket>
          )}

          {showMenu && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <ExerciseMenu
                index={exercise.index}
                amountExercises={amountExercises}
                showRocket={exercise.showRocket}
                sortedExercises={sortedExercises}
                zoomFactor={zoomFactor}
              />
            </motion.div>
          )}
        </ExerciseContainer>
        <ExercisePoints zoomfactor={zoomFactor}>
          <PointsContainer zoomfactor={zoomFactor}>
            /
            <PointsTextField
              zoomfactor={zoomFactor}
              id="outlined-basic"
              variant="filled"
              value={exercise.points === 0 ? "" : exercise.points}
              onChange={(e) => handleChangePoints(e.target.value)}
              onBlur={(e) => checkInputHasValue(e.target.value)}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 2,
              }}
            />
          </PointsContainer>
        </ExercisePoints>
      </Box>
    </>
  );
};

export default AllalinhornExercise;
